import React from "react";
import { Models } from "@hex-insights/app-modules";
import { makeClassName } from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	BooleanField,
	DateTimeField,
	FileUploadField,
	FormType,
	SubFormField,
	SubFormRenderProps,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
} from "@hex-insights/forms";
import { CalendarEventDetailQuery } from "../../../Utilities";
import { EditSharingMenu, NestedCreateSharingMenu } from "../../Components/SharingRecord";
import { CalendarEventFormValues, MediaAttachmentFormValues, SharingRecordCustomFormValues } from "../../FormUtilities";
import { MediaAttachmentCustomForm } from "../MediaAttachment";
import { BaseFieldProps } from "../shared";
import { UserSelectField } from "../User";
import styles from "./field.module.css";

/**
 * Generic props for fields of the CalendarEvent model.
 */
type FieldProps<K extends keyof CalendarEventFormValues.Base = keyof CalendarEventFormValues.Base> = BaseFieldProps<
	Pick<CalendarEventFormValues.Base, K>
>;

/**
 * Generic props for fields of the CalendarEvent model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof CalendarEventFormValues.Create = keyof CalendarEventFormValues.Create> =
	BaseFieldProps<Pick<CalendarEventFormValues.Create, K>>;

/**
 * Generic props for fields of the CalendarEvent model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof CalendarEventFormValues.Detail = keyof CalendarEventFormValues.Detail> =
	BaseFieldProps<Pick<CalendarEventFormValues.Detail, K>>;

/**
 * Field component for the `name` field of the CalendarEvent model.
 */
export function Name({ formState, className, ...props }: FieldProps<"name">) {
	return (
		<TextField formState={formState} name="name" className={makeClassName(styles["name"], className)} {...props} />
	);
}

/**
 * Field component for the `description` field of the CalendarEvent model.
 */
export function Description({ formState, ...props }: FieldProps<"description">) {
	return <TextField formState={formState} name="description" multiLine optional Input={DescriptionInput} {...props} />;
}

function DescriptionInput(props: TextFieldInputProps) {
	return <TextAreaInput minRows={4} {...props} />;
}

/**
 * Field component for the `location` field of the CalendarEvent model.
 */
export function Location({ formState, ...props }: FieldProps<"location">) {
	return <TextField formState={formState} name="location" optional {...props} />;
}

/**
 * Field component for the `image` field of the CalendarEvent model.
 */
export function Image({
	formState,
	url,
	className,
	...props
}: FieldProps<"imageKey" | "imageContentType"> & { url?: string }) {
	Models.requireURLForUpdateForm(url, false, formState.formType, "Image");

	return (
		<FileUploadField
			formState={formState}
			keyFieldName="imageKey"
			contentTypeFieldName="imageContentType"
			url={url}
			optional
			imagePreviewSizing="cover"
			className={makeClassName(styles["image"], className)}
			{...props}
		/>
	);
}

/**
 * Field component for the `isAllDay` field of the CalendarEvent model.
 */
export function IsAllDay({ formState, label = "All Day", ...props }: FieldProps<"isAllDay">) {
	return <BooleanField formState={formState} name="isAllDay" label={label} {...props} />;
}

/**
 * Field component for the `startTime` field of the CalendarEvent model.
 */
export function StartTime({ formState, ...props }: FieldProps<"startTime">) {
	return <DateTimeField formState={formState} name="startTime" precision="minute" withPreview={false} {...props} />;
}

/**
 * Field component for the `endTime` field of the CalendarEvent model.
 */
export function EndTime({ formState, ...props }: FieldProps<"endTime">) {
	return <DateTimeField formState={formState} name="endTime" precision="minute" withPreview={false} {...props} />;
}

/**
 * Field component for the `startDate` field of the CalendarEvent model.
 */
export function StartDate({ formState, ...props }: FieldProps<"startDate">) {
	return <DateTimeField formState={formState} name="startDate" precision="day" withPreview={false} {...props} />;
}

/**
 * Field component for the `isMultiDay` field of the CalendarEvent model.
 */
export function IsMultiDay({ formState, ...props }: FieldProps<"isMultiDay">) {
	return <BooleanField formState={formState} name="isMultiDay" {...props} />;
}

/**
 * Field component for the `endDate` field of the CalendarEvent model.
 */
export function EndDate({ formState, ...props }: FieldProps<"endDate">) {
	return <DateTimeField formState={formState} name="endDate" precision="day" withPreview={false} {...props} />;
}

/**
 * Field component for the `allowsResponses` field of the CalendarEvent model.
 */
export function AllowsResponses({
	formState,
	label = "Allow RSVPs",
	hint = "Allow invitees to RSVP to this event.",
	...props
}: FieldProps<"allowsResponses">) {
	return <BooleanField formState={formState} name="allowsResponses" label={label} hint={hint} {...props} />;
}

/**
 * Field component for the `createdAt` field of the Post model.
 */
export function CreatedAt({ formState, ...props }: DetailFieldProps<"createdAt">) {
	return (
		<DateTimeField
			formState={formState}
			name="createdAt"
			optional={FormType.isCreate(formState.formType)}
			precision="minute"
			{...props}
		/>
	);
}

export namespace Author {
	export type SelectProps = DetailFieldProps<"authorID"> &
		Omit<UserSelectField.SelectProps<CalendarEventFormValues.Detail>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `author` edge of the CalendarEvent model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <UserSelectField.Select formState={formState} name="authorID" optional {...props} />;
	}
}

export namespace MediaAttachments {
	/**
	 * SubForm field component for the `mediaAttachments` edge of the TestModel model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"mediaAttachments">) {
		return (
			<MediaAttachmentCustomForm.SubForm
				formState={formState}
				name="mediaAttachments"
				blankItem={MediaAttachmentFormValues.blankItemInCalendarEvent}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</MediaAttachmentCustomForm.SubForm>
		);
	}

	SubForm.SubForm = function (props: SubFormRenderProps<MediaAttachmentFormValues.CreateInCalendarEvent>) {
		return (
			<MediaAttachmentCustomForm.SubForm.SubForm {...props}>
				<MediaAttachmentCustomForm.CreateFieldsInCalendarEvent formState={props.formState} />
			</MediaAttachmentCustomForm.SubForm.SubForm>
		);
	};

	SubForm.AddButton = function (
		props: AddSubFormButtonRenderProps<
			Pick<CalendarEventFormValues.Create, "mediaAttachments">,
			MediaAttachmentFormValues.CreateInCalendarEvent
		>,
	) {
		return <MediaAttachmentCustomForm.SubForm.AddButton {...props} />;
	};
}

export namespace SharingRecord {
	/**
	 * SubForm field component for the `sharingRecord` edge of the CalendarEvent model.
	 */
	export function SubForm({ formState, noHeader = true, className, ...props }: CreateFieldProps<"sharingRecord">) {
		return (
			<SubFormField
				formState={formState}
				name="sharingRecord"
				blankItem={SharingRecordCustomFormValues.blankItemInParent}
				minItems={1}
				maxItems={1}
				formattedBlank=""
				noHeader={noHeader}
				className={makeClassName("sharing-record-sub-form-field", className)}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({ formState }: SubFormRenderProps<SharingRecordCustomFormValues.CreateInParent>) {
		return <NestedCreateSharingMenu formState={formState} />;
	};

	SubForm.AddButton = function (
		_: AddSubFormButtonRenderProps<
			Pick<CalendarEventFormValues.Create, "sharingRecord">,
			SharingRecordCustomFormValues.CreateInParent
		>,
	) {
		return <React.Fragment></React.Fragment>;
	};

	export type DetailProps = {
		sharingRecord: CalendarEventDetailQuery["calendarEvent"]["sharingRecord"];
	};

	export function Detail({ sharingRecord }: DetailProps) {
		if (!sharingRecord) {
			return null;
		}

		return <EditSharingMenu sharingRecord={sharingRecord} />;
	}
}
